import { ChildRef } from '../../../../../lib/web/components/child-ref';
import { Component } from '../../../../../lib/web/components/component';
import { ComponentBase } from '../../../../../lib/web/components/component-base';
import { Input } from '../../../../../lib/web/components/input';
import { EjsService } from '../../../services/ejs.service';
import { CardNavigationService } from '../../../services/card-navigation.service';
import { UserWatchingHistoryService } from '../../../services/user-watching-history.service';
import { ClickListener } from '../../../../../lib/web/components/click-listener';
import { DataEventService } from '../../../services/data-event.service';

const PAGE_ERROR_RETRY_DELAY: number = 5000;

@Component({
    selector: '.o-grid'
})
export class GridComponent extends ComponentBase<HTMLElement> {

    private _loadingMore: boolean = false;
    private _pagingObserver: IntersectionObserver = null;
    
    @Input('currentPage')
    private _currentPage: number = null;
    @Input('totalPages')
    private _totalPages: number = null;  
    @Input('format')
    private _format: string = null;  

    @ChildRef('.o-grid__items')
    private _itemsElement: HTMLDivElement = null;
    @ChildRef('.o-grid__loader')
    private _loaderElement: HTMLDivElement = null;
    @ChildRef('.o-grid__sentinel')
    private _sentinelElement: HTMLDivElement = null;

    public constructor(node: HTMLElement, 
        private _ejsService: EjsService, 
        private _cardNavigationService: CardNavigationService,
        private _userWatchingHistoryService: UserWatchingHistoryService,
        private _dataEventService: DataEventService) {

        super(node);

        this.addWindowEventListener('user-watching-history-changed', () => this.loadUserProgress());
    }    

    public onInit(): void {
        this._pagingObserver = new IntersectionObserver((entries: IntersectionObserverEntry[]) => {
            if (entries[0]?.isIntersecting) {
                this.loadMore();
            }
        }, { rootMargin: '200px' });
        this.enablePagingObserver();
        this._cardNavigationService.enableGridNavigation(this._node);
    }    

    @ClickListener('.o-grid__item-content')
    public onViewContent(event: MouseEvent): void {
        const item: HTMLAnchorElement = event.currentTarget as HTMLAnchorElement;        
        this._dataEventService.viewContent(new URL(item.href).pathname);
    }

    private loadUserProgress(): void {
        const items: HTMLDivElement[] = Array.from(this._itemsElement.querySelectorAll('.o-grid__item'));
        items?.forEach(item => {
            const id: number = parseInt(item.dataset.id);
            const watchingHistory: any = ['capsule', 'collection'].includes(this.getItemContentType(item)) ? this._userWatchingHistoryService.getItem(id, this.getItemContentType(item) as 'capsule' | 'collection') : null;
            this.setProgress(item, watchingHistory);
        });
    }

    public async loadMore(): Promise<void> {
        if (!this._loadingMore && this._currentPage < this._totalPages) {
            this._loadingMore = true; 
            this.disablePagingObserver();            
            this.showLoader();
            this.dispatchCustomEvent('load-page', { page: this._currentPage + 1 });
        }
    }
    
    public setPageResponse(items: any[], pageInfo: { currentPage: number, totalPages: number }): void {
        const { currentPage, totalPages } = pageInfo;
        if (items && items.length) {
            const html: string = items.map((i, index) => this._ejsService.render('organisms/grid/grid-item', { item: i, format: this._format, colindex: index + 1 })).join('');
            this._itemsElement.innerHTML += html;
            this.fragment();
            // Re-enable navigation after new content is added
            this._cardNavigationService.enableGridNavigation(this._node);
            this.loadUserProgress();
        }
        this._currentPage = currentPage;
        this._totalPages = totalPages;
        this.hideLoader();
        this._loadingMore = false;
        this.enablePagingObserver();
    }

    public setPageError(): void {
        this.hideLoader();
        this._loadingMore = false;
        // Reactiva la paginación con retardo para no reintentar en bucle si no hay red
        setTimeout(() => !this.isDisposed && this.enablePagingObserver(), PAGE_ERROR_RETRY_DELAY);
    }

    public get itemElements(): HTMLDivElement[] {
        return Array.from(this._node.querySelectorAll('.o-grid__item'));
    }

    public setProgress(itemElement: HTMLDivElement, data: { current: number, duration: number }): void {        
        if (itemElement) {
            const { current, duration } = data || {};
            if (current) {
                const progressValueElement: HTMLDivElement = itemElement.querySelector('.o-grid__item-progress-value');
                if (progressValueElement) {
                    itemElement.classList.add('o-grid__item--with-progress');
                    progressValueElement.style.width = `${((current || 0) / (duration || 1)) * 100}%`;
                }
            }
            else {
                itemElement.classList.remove('o-grid__item--with-progress');
            }
        }
    }

    public getItemContentType(itemElement: HTMLDivElement): string {
        if (['video', 'podcast'].includes(itemElement.dataset.type)) {
            return 'capsule';
        }
        else {
            return itemElement.dataset.type;
        }
    }

    public reset(): void {
        this._loadingMore = false;
        this._currentPage = 0;
        this._totalPages = 1;
        this.setInnerHTML('', '.o-grid__items');
        this.hideLoader();
        this.loadMore();
    }

    private enablePagingObserver(): void {
        if (this._sentinelElement) {
            this._pagingObserver?.observe(this._sentinelElement);
        }
    }

    private disablePagingObserver(): void {
        if (this._sentinelElement) {
        this._pagingObserver?.unobserve(this._sentinelElement);
        }
    }

    public showLoader(): void {
        if (this._loaderElement) {
            this._loaderElement.hidden = false;
        }
    }

    public hideLoader(): void {
        if (this._loaderElement) {
            this._loaderElement.hidden = true;
        }
    }

    public dispose(): void {
        this._pagingObserver?.disconnect();
        this._cardNavigationService.disableNavigation(this._node);
        super.dispose();
    }
}